import { observer } from 'owa-mobx-react';
import { readingPane, skipToMessage } from './MailView.locstring.json';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { ListPane } from 'owa-mail-list-pane-view';
import {
    getSelectedTableView,
    getComputedSelectedRowKeysSize,
    getIsSearchTableShown,
    isMyOutlookTable,
} from 'owa-mail-list-store';
import {
    getListViewDimensions,
    setListViewDimension,
    isReadingPanePositionBottom,
    isReadingPanePositionRight,
    isReadingPanePositionOff,
    shouldShowListView,
    shouldShowReadingPane,
    getListViewContainerStyles,
    getListViewPlusCardContainerStyles,
    getReadingPaneContainerStyles,
    shouldShowFolderPane,
    getLeftNavWidth,
    shouldShowFavoritesSideRail,
} from 'owa-mail-layout';
import ReadingPaneContainer from 'owa-mail-reading-pane-container/lib/components/ReadingPaneContainer';
import { NotificationBarHost } from 'owa-notification-bar';
import { triggerResizeEvent } from 'owa-resize-event';
import { ResizeHandle, ResizeHandleDirection, ResizeHandleScenario } from 'owa-resize-handle';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { TabType, getActiveContentTab } from 'owa-tab-store';
import { TabBar } from 'owa-tab-view';
import React from 'react';
import getMailListHeader from 'owa-mail-list-pane-view/lib/components/getMailListHeader';
import getStyleSelectorAsPerUserSettings from 'owa-mail-list-view/lib/utils/getStyleSelectorAsPerUserSettings';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import { NotesPane } from 'owa-notes-components';
import folderStore from 'owa-folders';
import { resetFocus } from 'owa-mail-focus-manager';
import { MailHotkeysMap, getMailHotkeysMapProperties } from 'owa-mail-hotkeys';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { getIsNonMailEntitySearchTab, TabbedSearchPane } from 'owa-tabbed-search';
import { isDumpsterOrDumpsterSearchTable as getIsDumpsterOrDumpsterSearchTable } from '../utils/isDumpsterOrDumpsterSearchTable';
import { lazySetAppSections } from 'owa-accessibility-loop-nav';
import { lazyRegisterHotkeysMapPropertiesProvider } from 'owa-hotkeys-map';
import { OutlookTourHost } from 'tour-host';
import { type FolderForestNodeType } from 'owa-favorites-types';
import { isCapabilityEnabled } from 'owa-capabilities';
import { getApplicationSettings } from 'owa-application-settings';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import classNames from 'owa-classnames';
import { getIsDarkTheme } from 'owa-fabric-theme';
import getIsLightRPOn from 'owa-light-reading-pane-store/lib/selectors/getIsLightRPOn';
import {
    mailListWithRPRight,
    mailListWithRPHiddenOrBottom,
    mailListWithRPHiddenOrBottomDenseless,
    mailListWithRPHiddenOrBottomDenselessNeutral,
    mailListCardContainerWithRPHiddenOrBottom,
    mailListWithRPRightDenseless,
    mailListCardContainerWithRPHiddenOrBottomDenseless,
    mailListCardContainerWithRPHiddenOrBottomDenselessNeutral,
    mailListLeftPaneVisibleFullBleed,
    mailListLeftPaneVisibleFullBleedDenseless,
    mailListLeftPaneVisibleFullBleedDenselessLeftBorderRounded,
    mailListLeftPaneVisibleFullBleedDenselessLeftBorderRoundedRtl,
    mailListLeftPaneVisibleFullBleedDenselessRightBorderRounded,
    mailListLeftPaneVisibleFullBleedDenselessRightBorderRoundedRtl,
    mailListLeftPaneVisibleFullBleedDenselessNoTopMargin,
    mailListNeutralBorder,
    mailListNeutralTopBorder,
    mailListRefresh,
    mailListRefreshRtl,
    mailListRefreshNoLeftBorder,
    mailListRefreshNoLeftBorderRtl,
    bleedthroughBackgroundDark,
    bleedthroughBackgroundLight,
    mailListHide,
    subCommandBarPaneColumn,
    subCommandBarPaneRow,
    subCommandBarPaneCommon,
    subCommandBarPaneCommonRefresh,
    subCommandBarPaneMail,
    rightPane,
    rightPaneDenseless,
    readingPane as styles_readingPane,
    cardAndMLWrapper,
    cardAndMLWrapperBizChatSpacing,
    denselessCompose,
    mailListComposeRightBorderDenseless,
    denselessComposeBleedthrough,
    denselessComposeInnerNeutral,
    denselessComposeInnerSearch,
    denselessComposeInnerNeutralLeftBorderRounded,
    denselessComposeBleedthroughNoLeftBorder,
    resizeHandle,
    notificationBarHostElevated,
} from './MailModule.scss';
import type { SearchTableQuery } from 'owa-mail-list-search';
import { SearchPlusChatEntryPointContainer } from 'owa-mail-search-plus-chat';
import { LazyCopilotSearchAnswerContainer } from 'owa-mail-search-copilot-answer';
import { isBrowserSafari, isMinimumBrowserVersion } from 'owa-user-agent';
import { getStore as getMailSearchStore } from 'owa-mail-search';
import { isSearchPlusChatFeatureEnabledForSearchSession } from 'owa-mail-search/lib/utils/isSearchPlusChatFeatureEnabledForSearchSession';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import shouldRenderCopilotForSearchMailResults from 'owa-mail-list-store/lib/utils/shouldRenderCopilotForSearchMailResults';
import { getCurrentModule } from 'owa-app-module-store';
import { Module } from 'owa-workloads-module';
import { shouldShowBizChatInReadingPane } from 'owa-copilot-chat-drawer-store';
import { BlurredBackgroundView } from 'owa-blurred-view';
import { useRegisterNavigationLoopContainer } from 'owa-accessibility-loop-nav/lib/hooks/useRegisterNavigationLoopContainer';

const defaultExport: React.FunctionComponent = observer(function MailView() {
    const hasFavoritesSideRail = shouldShowFavoritesSideRail();
    React.useEffect(() => {
        lazySetAppSections.importAndExecute('Mail');
        lazyRegisterHotkeysMapPropertiesProvider.importAndExecute(
            Module.Mail,
            getMailHotkeysMapProperties
        );
    }, []);
    const tableView = getSelectedTableView();
    const hasItemSelected = isGlobalFeatureEnabled('fwk-ml-reduce-render-scope')
        ? getComputedSelectedRowKeysSize() > 0
        : tableView?.selectedRowKeys && tableView.selectedRowKeys.size > 0;
    const isDarkMode = getIsDarkTheme();
    const isLightRPOn = getIsLightRPOn();
    const isBlurredViewEnabled = isGlobalFeatureEnabled('rp-blurred-view');

    const isRPPositionRight = isReadingPanePositionRight();
    const isRPPositionBottom = isReadingPanePositionBottom();
    let showReadingPane = shouldShowReadingPane();
    let showListPane = shouldShowListView();
    // While leaving MyOutlook (Today), the empty MyOutlook blank table can
    // remain the selected table for a beat after the list pane is re-shown but
    // before selectFolder swaps in the destination folder's table. Rendering the
    // list in that window flashes the blank table's "no items" empty state. Keep
    // the list hidden until a real table is selected so the list reappears
    // already showing the destination folder (or its loading state).
    if (showListPane && isMyOutlookTable(tableView?.tableQuery)) {
        showListPane = false;
    }
    const showFolderPane = shouldShowFolderPane();
    const activeTab = getActiveContentTab();
    const isDumpsterOrDumpsterSearchTable = getIsDumpsterOrDumpsterSearchTable();
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isTypeRampUpdatesEnabled = isGlobalFeatureEnabled('mon-type-ramp');
    // @1js/scc-react-markdown-renderer (used by CopilotSearchAnswer) constructs regexes with
    // negative lookbehind at module-eval time, which throws on Safari < 16.4 and fails the
    // bundle load. Skip the lazy import on those Safari versions.
    // TODO: Remove this gate once @1js/scc-react-markdown-renderer drops the lookbehind regexes.
    // Tracked in https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/429095
    const supportsRegexLookbehind = !isBrowserSafari() || isMinimumBrowserVersion([16, 4]);

    const oldMailListClasses = classNames(
        isRPPositionRight ? mailListWithRPRight : mailListWithRPHiddenOrBottom,
        mailListRefresh, // Bug154909 -- remove SunValley "hacks" when 1js natively supports SV2.
        isBleedThroughEnabled && showFolderPane && mailListLeftPaneVisibleFullBleed,
        !showListPane && mailListHide,
        getListViewContainerStyles()
    );

    const isSearchTableShown = getIsSearchTableShown();

    const shouldShowSearchPlusChat = useComputedValue(() => {
        if (!tableView || getCurrentModule() !== Module.Mail) {
            return false;
        }
        return (
            isSearchTableShown &&
            shouldRenderCopilotForSearchMailResults(
                (tableView.tableQuery as SearchTableQuery).searchProvider
            ) &&
            isSearchPlusChatFeatureEnabledForSearchSession()
        );
    }, [tableView, isSearchTableShown]);

    const getMailListClasses = React.useCallback(() => {
        const isRTL = isCurrentCultureRightToLeft();

        // left radius is only applied when folder pane is not expanded
        // or when theme is not bleed through enabled and folder pane is expanded
        const leftRadiusDenselessClass =
            (!showFolderPane || !isBleedThroughEnabled) &&
            !isSearchTableShown &&
            mailListLeftPaneVisibleFullBleedDenselessLeftBorderRounded;
        const leftRadiusDenselessClassRtl =
            leftRadiusDenselessClass &&
            isRTL &&
            mailListLeftPaneVisibleFullBleedDenselessLeftBorderRoundedRtl;

        // right radius is only applied when there is nothing selected or RP is not on the right
        // and is not compose
        const rightRadiusDenselessClass =
            (!hasItemSelected || !isRPPositionRight) &&
            !isSearchTableShown &&
            activeTab?.type != TabType.MailCompose &&
            mailListLeftPaneVisibleFullBleedDenselessRightBorderRounded;
        const rightRadiusDenselessClassRtl =
            rightRadiusDenselessClass &&
            isRTL &&
            mailListLeftPaneVisibleFullBleedDenselessRightBorderRoundedRtl;

        const rightBorderDenselessMLForComposeClass =
            activeTab?.type === TabType.MailCompose &&
            isTypeRampUpdatesEnabled &&
            mailListComposeRightBorderDenseless;

        const noTopMarginClass =
            isTypeRampUpdatesEnabled &&
            isSearchTableShown &&
            mailListLeftPaneVisibleFullBleedDenselessNoTopMargin;

        const noLeftBorderActive =
            (!isTypeRampUpdatesEnabled && !showFolderPane) ||
            (isTypeRampUpdatesEnabled && !showFolderPane && isBleedThroughEnabled);

        return classNames(
            mailListRefresh,
            isRTL && mailListRefreshRtl,
            rightBorderDenselessMLForComposeClass,
            noTopMarginClass,
            !isBleedThroughEnabled &&
                isTypeRampUpdatesEnabled &&
                (!hasItemSelected || !isRPPositionRight
                    ? mailListNeutralBorder
                    : mailListNeutralTopBorder),
            mailListLeftPaneVisibleFullBleedDenseless,
            isTypeRampUpdatesEnabled && rightRadiusDenselessClass,
            isTypeRampUpdatesEnabled && rightRadiusDenselessClassRtl,
            isTypeRampUpdatesEnabled && leftRadiusDenselessClass,
            isTypeRampUpdatesEnabled && leftRadiusDenselessClassRtl,
            isRPPositionRight && mailListWithRPRight,
            isRPPositionRight
                ? mailListWithRPRightDenseless
                : isBleedThroughEnabled
                ? mailListWithRPHiddenOrBottomDenseless
                : mailListWithRPHiddenOrBottomDenselessNeutral,
            noLeftBorderActive && mailListRefreshNoLeftBorder,
            noLeftBorderActive && isRTL && mailListRefreshNoLeftBorderRtl,
            !showListPane && mailListHide,
            isBleedThroughEnabled &&
                (isDarkMode ? bleedthroughBackgroundDark : bleedthroughBackgroundLight),
            getListViewContainerStyles()
        );
    }, [
        showFolderPane,
        isTypeRampUpdatesEnabled,
        isBleedThroughEnabled,
        hasItemSelected,
        isRPPositionRight,
        isRPPositionBottom,
        showListPane,
        isDarkMode,
        shouldShowSearchPlusChat,
        activeTab,
        isSearchTableShown,
    ]);

    // Adjust list view & reading pane state for tab view
    if (activeTab && !isDumpsterOrDumpsterSearchTable) {
        if (isRPPositionBottom) {
            // When reading pane is on bottom and current tab is full compose, we need to hide list view
            // to give compose enough space.
            // Here we only check TabType.MailCompose. There are still 2 cases that compose can be show but
            // we don't check them here, because:
            // 1. current tab is Primary tab and we have primary compose. This happens when user is open
            // draft items folder. In that case we still show list view otherwise user can never see list
            // view of draft folder.
            // 2. inline compose. This should never happen when reading pane is on bottom.
            showListPane = showListPane && activeTab.type != TabType.MailCompose;
        } else if (activeTab.type != TabType.Primary) {
            // Non-primary tabs render in the reading pane container. Keep the container mounted
            // while switching RP positions so compose/secondary RP tabs resize instead of remounting.
            showReadingPane = true;
            showListPane = isRPPositionRight ? showListPane : false;
        }
    }
    // When context menu is opened for a message, do not show reading pane, but show tab bar in list view only if
    // reading pane position is off in user setting. (Bug155397)
    const showTabBarInListView =
        showListPane &&
        !showReadingPane &&
        isReadingPanePositionOff() &&
        !isDumpsterOrDumpsterSearchTable;
    const tabBar = React.useMemo(
        () => (showTabBarInListView ? <TabBar /> : null),
        [showTabBarInListView]
    );

    if (!tableView) {
        return null;
    }
    const tableViewId = tableView.id;
    const styleSelectorAsPerUserSettings = getStyleSelectorAsPerUserSettings(tableViewId);
    const showTabBarInReadingPane = !showTabBarInListView && !isDumpsterOrDumpsterSearchTable;
    const cardAndMLWrapperClasses = classNames(
        cardAndMLWrapper,
        !showListPane && mailListHide,
        getListViewPlusCardContainerStyles(hasItemSelected),
        // Add spacing when BizChat is docked in reading pane and search is NOT shown
        isTypeRampUpdatesEnabled &&
            shouldShowBizChatInReadingPane(window) &&
            !isSearchTableShown &&
            cardAndMLWrapperBizChatSpacing,
        // In bottom RP, elevate the list wrapper so the notification toast hosted inside it (see
        // hostNotificationInListPane) paints above the reading pane instead of behind it.
        isRPPositionBottom && notificationBarHostElevated,
        resizeHandle
    );
    const subCommandBarPaneClasses = classNames(
        isRPPositionBottom ? subCommandBarPaneColumn : subCommandBarPaneRow,
        subCommandBarPaneCommon,
        subCommandBarPaneCommonRefresh, // Bug154909 -- remove SunValley "hacks" when 1js natively supports SV2.
        subCommandBarPaneMail
    );
    const showNotificationBar = !isRPPositionRight;
    const isSearchTable = getIsSearchTableShown();
    const showPersonaHeader = getSelectedNode().type === 1 && !isSearchTable;
    const showSearchRefinersExtendedHeader = isSearchTable && !showPersonaHeader;
    const shouldMailViewRenderHeader = showSearchRefinersExtendedHeader && isRPPositionRight;
    const isNotesFolder = folderIdToName(tableView?.tableQuery?.folderId ?? '') === 'notes';
    const sourceFolder = tableView && folderStore().folderTable.get(tableView.tableQuery.folderId);
    const showTabbedSearchPane = getIsNonMailEntitySearchTab();
    // The toast must live in whichever focus-trap section holds focus after a triage action, so it
    // stays Tab-reachable. When the list is visible (RP-off/bottom list view, or the always-present
    // list in non-right layouts) host it in ListPane's MessageList section; in bottom RP the list
    // wrapper is elevated (see cardAndMLWrapperClasses) so the in-list toast paints above the
    // reading pane. showNotificationBar === !isReadingPanePositionRight().
    // In tabbed-search mode TabbedSearchPane replaces the entire ML+RP subtree, so neither the
    // MessageList nor the MailReadCompose host container is rendered. Both flags therefore require
    // !showTabbedSearchPane and the toast falls through to the elevated host below; that host stays
    // Tab-reachable here because TabbedSearchPane isn't a registered focus-trap section.
    const hostNotificationInListPane = showNotificationBar && showListPane && !showTabbedSearchPane;
    // When the reading pane is the immersive view (item open with the list hidden, e.g. RP-off +
    // open item, or bottom RP + full compose) focus is trapped in the MailReadCompose section, so
    // host the toast there (wrapped in notificationBarHostElevated to paint above ReadingPaneContainer's
    // z-index:1 stacking context) instead of the elevated host, which is outside every section and
    // thus not Tab-reachable. Excludes the BizChat-docked case, which doesn't render ReadingPaneWrapper.
    const hostNotificationInReadingPane =
        showNotificationBar &&
        showReadingPane &&
        !showListPane &&
        !showTabbedSearchPane &&
        !shouldShowBizChatInReadingPane(window);

    const mailListClasses = isTypeRampUpdatesEnabled ? getMailListClasses() : oldMailListClasses;

    const shouldListPaneRenderHeader = () => {
        if (isSearchTable) {
            if (isGlobalFeatureEnabled('sea-searchRibbon')) {
                return true;
            } else {
                return false;
            }
        } else {
            return !shouldMailViewRenderHeader;
        }
    };

    if (
        !!sourceFolder &&
        folderIdToName(sourceFolder.id) === 'notes' &&
        isGlobalFeatureEnabled('notes-folder-view')
    ) {
        if (window.performance) {
            window.performance.mark('StartStickyNotesSDKLoad');
        }
        return (
            <NotesPane
                scenario="NotesFolder"
                header={getMailListHeader(tableViewId, undefined) || undefined}
            />
        );
    }
    return (
        <div
            className={classNames(
                isTypeRampUpdatesEnabled && !isBleedThroughEnabled && !hasFavoritesSideRail
                    ? rightPaneDenseless
                    : rightPane
            )}
        >
            <div className={subCommandBarPaneClasses}>
                {showTabbedSearchPane ? (
                    <TabbedSearchPane header={undefined} />
                ) : (
                    <>
                        <div className={cardAndMLWrapperClasses} data-app-section="MessageList">
                            {shouldShowSearchPlusChat &&
                                tableView &&
                                !getMailSearchStore().isQueryNL && (
                                    <SearchPlusChatEntryPointContainer
                                        isDarkMode={isDarkMode}
                                        key="entryPoint_0"
                                        tableView={tableView}
                                        isRPPositionRight={isRPPositionRight}
                                        isBleedThroughEnabled={isBleedThroughEnabled}
                                        customClassName={classNames(
                                            isTypeRampUpdatesEnabled
                                                ? isRPPositionRight
                                                    ? mailListWithRPRightDenseless
                                                    : isBleedThroughEnabled
                                                    ? mailListCardContainerWithRPHiddenOrBottomDenselessNeutral
                                                    : mailListCardContainerWithRPHiddenOrBottomDenseless
                                                : undefined,
                                            !isTypeRampUpdatesEnabled
                                                ? isRPPositionRight
                                                    ? undefined
                                                    : mailListCardContainerWithRPHiddenOrBottom
                                                : undefined
                                        )}
                                    />
                                )}
                            {shouldShowSearchPlusChat &&
                                getMailSearchStore().isQueryNL &&
                                supportsRegexLookbehind && (
                                    <LazyCopilotSearchAnswerContainer
                                        customClassName={classNames(
                                            isTypeRampUpdatesEnabled
                                                ? isRPPositionRight
                                                    ? mailListWithRPRightDenseless
                                                    : isBleedThroughEnabled
                                                    ? mailListCardContainerWithRPHiddenOrBottomDenselessNeutral
                                                    : mailListCardContainerWithRPHiddenOrBottomDenseless
                                                : undefined,
                                            !isTypeRampUpdatesEnabled
                                                ? isRPPositionRight
                                                    ? undefined
                                                    : mailListCardContainerWithRPHiddenOrBottom
                                                : undefined
                                        )}
                                    />
                                )}
                            <ListPane
                                className={mailListClasses}
                                tableViewId={tableViewId}
                                shouldShowHeader={shouldListPaneRenderHeader()} // ListPane renders a header only if MailView hasn't
                                tabBar={tabBar}
                                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                                isBleedThroughEnabled={isBleedThroughEnabled}
                                shouldHostNonRightNotificationBar={hostNotificationInListPane}
                            />
                        </div>
                        {showReadingPane && showListPane && (
                            <ResizeHandle
                                direction={
                                    isRPPositionBottom
                                        ? ResizeHandleDirection.horizontal
                                        : ResizeHandleDirection.vertical
                                }
                                onResized={onRightPaneResized}
                                scenario={ResizeHandleScenario.ReadingPane}
                                onResizeViaKeyboardCompleted={onReadingPaneResizeCompleted}
                                className={resizeHandle}
                            />
                        )}
                        {showReadingPane && !shouldShowBizChatInReadingPane(window) && (
                            <ReadingPaneWrapper>
                                {isTypeRampUpdatesEnabled &&
                                    activeTab?.type === TabType.MailCompose && (
                                        <BlurredBackgroundView
                                            className={
                                                isBleedThroughEnabled
                                                    ? denselessComposeBleedthrough
                                                    : denselessCompose
                                            }
                                            gradientClassName={classNames(
                                                isBleedThroughEnabled
                                                    ? showFolderPane
                                                        ? denselessComposeBleedthrough
                                                        : denselessComposeBleedthroughNoLeftBorder
                                                    : denselessCompose,
                                                !isBleedThroughEnabled &&
                                                    denselessComposeInnerNeutral,
                                                isSearchTableShown &&
                                                    isTypeRampUpdatesEnabled &&
                                                    denselessComposeInnerSearch,
                                                ((!isBleedThroughEnabled && !isRPPositionRight) ||
                                                    (!showFolderPane && !isRPPositionRight)) &&
                                                    denselessComposeInnerNeutralLeftBorderRounded
                                            )}
                                            isDarkTheme={isDarkMode && !isLightRPOn}
                                            isBlurred={isBlurredViewEnabled}
                                        />
                                    )}
                                <ReadingPaneContainer
                                    isDumpsterOrDumpsterSearchTable={
                                        isDumpsterOrDumpsterSearchTable
                                    }
                                    isNotesFolder={isNotesFolder}
                                />
                                {hostNotificationInReadingPane && (
                                    <div className={notificationBarHostElevated}>
                                        <NotificationBarHost
                                            hostId={'MailModuleNotificationBarHost'}
                                            onNotificationBlur={onNotificationBlurCallback}
                                            positionOffset={'bottom'}
                                            leftNavWidth={getLeftNavWidth()}
                                            listViewWidth={getListViewDimensions().listViewWidth}
                                            isFolderPaneExpanded={showFolderPane}
                                        />
                                    </div>
                                )}
                                {showTabBarInReadingPane && (
                                    <TabBar isDenseless={isTypeRampUpdatesEnabled} />
                                )}
                            </ReadingPaneWrapper>
                        )}
                    </>
                )}
                {showNotificationBar &&
                    !hostNotificationInListPane &&
                    !hostNotificationInReadingPane && (
                        <div className={notificationBarHostElevated}>
                            <NotificationBarHost
                                hostId={'MailModuleNotificationBarHost'}
                                onNotificationBlur={onNotificationBlurCallback}
                                positionOffset={'bottom'}
                                leftNavWidth={getLeftNavWidth()}
                                listViewWidth={getListViewDimensions().listViewWidth}
                                isFolderPaneExpanded={showFolderPane}
                            />
                        </div>
                    )}
                {getApplicationSettings('TipsTour').enabled && <OutlookTourHost />}
            </div>
            {isGlobalFeatureEnabled('acct-fullOptionsProjectionPopout') ? undefined : (
                <MailHotkeysMap />
            )}
        </div>
    );
}, 'MailView');
export default defaultExport;
function onRightPaneResized(listViewDimension: number) {
    const isWidth = isReadingPanePositionRight();
    setListViewDimension(listViewDimension, isWidth);
    triggerResizeEvent();
}

function onReadingPaneResizeCompleted() {
    resetFocus('onReadingPaneResizeCompleted');
}

function onNotificationBlurCallback() {
    resetFocus('MailViewNotificationBarHost');
}

interface ReadingPaneWrapperProps {
    children: React.ReactNode;
}

function ReadingPaneWrapper({ children }: ReadingPaneWrapperProps) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    useRegisterNavigationLoopContainer('MailReadCompose', containerRef);

    return (
        <SkipLinkRegion
            ref={containerRef}
            skipLinkName={loc(skipToMessage)}
            role="main"
            regionName={loc(readingPane)}
            className={classNames(styles_readingPane, getReadingPaneContainerStyles())}
            data-app-section="MailReadCompose"
        >
            {children}
        </SkipLinkRegion>
    );
}
